import { useParams } from 'react-router-dom';
import { useVisaApplicationGet } from '../hooks/use-visa-application.hook';
import { memo, useMemo } from 'react';
import VisaApplicationDetailsComponent, {
  SectionData,
} from '../components/visa-application-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { VisaApplication } from '../types/visa-application.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@utils/date.util';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import PageTitle from '@components/common/PageTitle';

const VisaApplicationDetailsContainer = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { data, isLoading, error } = useVisaApplicationGet(id || '');

  const prepareDetailsData = useMemo(
    () =>
      (dataObj: VisaApplication | undefined): SectionData[] => {
        if (!dataObj) return [];

        const sections: SectionData[] = [];

        // Helper function to add items to a section
        const addItem = (sectionItems: DetailItem[], label: string, value: any, isDate = false) => {
          if (value === undefined || value === null) return;
          sectionItems.push({
            label,
            value: isDate ? formatDate(value) : String(value),
          });
        };

        // Top fields section
        const topItems: DetailItem[] = [];
        addItem(topItems, t('visaApplications.passportNumber.label'), dataObj.passportNumber);
        addItem(topItems, t('visaApplications.applicationNumber.label'), dataObj.applicationNumber);
        addItem(topItems, t('visaApplications.visaType.label'), dataObj.visaType);
        addItem(topItems, t('visaApplications.visaName.label'), dataObj.visaName);
        addItem(topItems, t('visaApplications.visaSponsor.label'), dataObj.visaSponsor);
        // Handle visaStatus - it might be a Status object or a string
        const visaStatusDisplay =
          typeof dataObj.visaStatus === 'object' && dataObj.visaStatus !== null
            ? (dataObj.visaStatus as any).description || (dataObj.visaStatus as any).code || 'N/A'
            : dataObj.visaStatus || 'N/A';
        addItem(topItems, t('visaApplications.visaStatus.label'), visaStatusDisplay);
        addItem(topItems, t('visaApplications.visaIssueDate.label'), dataObj.visaIssueDate, true);
        addItem(topItems, t('visaApplications.visaExpiryDate.label'), dataObj.visaExpiryDate, true);
        if (topItems.length > 0) {
          sections.push({ title: t('visaApplications.title'), items: topItems });
        }

        // General Information section
        const generalItems: DetailItem[] = [];
        addItem(generalItems, 'MRZ', dataObj.mrz);
        addItem(generalItems, 'Visa UID', dataObj.visaUidNumber);
        addItem(generalItems, 'Visa File Number', dataObj.visaFileNumber);
        if (generalItems.length > 0) {
          sections.push({ title: t('common.generalInformation'), items: generalItems });
        }

        // Client Data
        const clientItems: DetailItem[] = [];
        if (dataObj.clientData) {
          addItem(clientItems, 'Client Name', dataObj.clientData.clientName);
          addItem(clientItems, 'Org Level 1', dataObj.clientData.orgLevel1);
          addItem(clientItems, 'Org Level 2', dataObj.clientData.orgLevel2);
          addItem(clientItems, 'Org Level 3', dataObj.clientData.orgLevel3);
          addItem(clientItems, 'Org Level 4', dataObj.clientData.orgLevel4);
          addItem(clientItems, 'Org Level 5', dataObj.clientData.orgLevel5);
          addItem(clientItems, 'Org Level 6', dataObj.clientData.orgLevel6);
          addItem(clientItems, 'Org Level 7', dataObj.clientData.orgLevel7);
          addItem(clientItems, 'Trade License Number', dataObj.clientData.tradeLicenseNumber);
          addItem(
            clientItems,
            'Trade License Issue Date',
            dataObj.clientData.tradeLicenseIssueDate,
            true
          );
          addItem(
            clientItems,
            'Trade License Expiry Date',
            dataObj.clientData.tradeLicenseExpiryDate,
            true
          );
          addItem(clientItems, 'Est Card Number', dataObj.clientData.estCardNumber);
          addItem(clientItems, 'Est Card Issue Date', dataObj.clientData.estCardIssueDate, true);
          addItem(clientItems, 'Est Card Expiry Date', dataObj.clientData.estCardExpiryDate, true);
          addItem(clientItems, 'Company Address', dataObj.clientData.companyAddress);
        }
        if (clientItems.length > 0) {
          sections.push({ title: 'Client', items: clientItems });
        }

        // Application Data
        const applicationItems: DetailItem[] = [];
        if (dataObj.applicationData) {
          addItem(applicationItems, 'Service Name', dataObj.applicationData.serviceName);
          addItem(applicationItems, 'Service Stage', dataObj.applicationData.serviceStage);
          addItem(applicationItems, 'Service Outcome', dataObj.applicationData.serviceOutcome);
          addItem(applicationItems, 'Trigger Time', dataObj.applicationData.triggerTime, true);
          addItem(
            applicationItems,
            'Execution Start',
            dataObj.applicationData.executionStartTime,
            true
          );
          addItem(
            applicationItems,
            'Execution End',
            dataObj.applicationData.executionEndTime,
            true
          );
          addItem(
            applicationItems,
            'Execution Duration (min)',
            dataObj.applicationData.executionDuration
          );
          addItem(applicationItems, 'Document Number', dataObj.applicationData.documentNumber);
          addItem(applicationItems, 'Failure Category', dataObj.applicationData.failureCategory);
          addItem(applicationItems, 'Failure Detail', dataObj.applicationData.failureDetail);
          addItem(applicationItems, 'Failure Count', dataObj.applicationData.failureCount);
          addItem(applicationItems, 'Machine ID', dataObj.applicationData.machineId);
          addItem(applicationItems, 'Incident ID', dataObj.applicationData.incidentId);
        }
        if (applicationItems.length > 0) {
          sections.push({ title: 'Application', items: applicationItems });
        }

        // Employee Data
        const employeeItems: DetailItem[] = [];
        if (dataObj.employeeData) {
          addItem(employeeItems, 'Candidate ID', dataObj.employeeData.candidateId);
          addItem(employeeItems, 'Employee ID', dataObj.employeeData.employeeId);
          addItem(employeeItems, 'Probot ID', dataObj.employeeData.probotId);
          addItem(
            employeeItems,
            'Employment Start (Est)',
            dataObj.employeeData.employmentStartDateEstimate,
            true
          );
          addItem(
            employeeItems,
            'Employment Start (Actual)',
            dataObj.employeeData.employmentStartDateActual,
            true
          );
          addItem(employeeItems, 'Employment End', dataObj.employeeData.employmentEndDate, true);
          addItem(employeeItems, 'Home Address', dataObj.employeeData.addressHome);
          addItem(employeeItems, 'Local Address', dataObj.employeeData.addressLocal);
          addItem(employeeItems, 'Home Phone', dataObj.employeeData.phoneHome);
          addItem(employeeItems, 'Education Level', dataObj.employeeData.educationLevel);
          addItem(
            employeeItems,
            'Education Description',
            dataObj.employeeData.educationDescription
          );
          addItem(employeeItems, 'Marital Status', dataObj.employeeData.maritalStatus);
          addItem(employeeItems, 'First Language', dataObj.employeeData.firstLanguage);
          addItem(employeeItems, 'Number of Children', dataObj.employeeData.numberOfChildren);
          addItem(employeeItems, 'Mother Name', dataObj.employeeData.motherName);
          addItem(employeeItems, 'Father Name', dataObj.employeeData.fatherName);
          addItem(employeeItems, 'Typed First Name', dataObj.employeeData.typedFirstName);
          addItem(employeeItems, 'Typed Middle Name', dataObj.employeeData.typedMiddleName);
          addItem(employeeItems, 'Typed Last Name', dataObj.employeeData.typedLastName);
          addItem(employeeItems, 'Date of Birth', dataObj.employeeData.typedDateOfBirth, true);
          addItem(employeeItems, 'Gender', dataObj.employeeData.typedGender);
          addItem(employeeItems, 'Nationality', dataObj.employeeData.typedNationality);
          addItem(employeeItems, 'Biodata Verified', dataObj.employeeData.biodataVerified);
          addItem(employeeItems, 'Company Designation', dataObj.employeeData.companyDesignation);
          addItem(employeeItems, 'Mapped Designation', dataObj.employeeData.mappedDesignation);
          addItem(employeeItems, 'Work Type', dataObj.employeeData.workType);
          addItem(employeeItems, 'Previous Nationality', dataObj.employeeData.previousNationality);
          addItem(
            employeeItems,
            'Need Address in UAE?',
            dataObj.employeeData.askingForAddressInUae
          );
          addItem(employeeItems, 'Need Mobile Number?', dataObj.employeeData.askingForMobileNumber);
        }
        if (employeeItems.length > 0) {
          sections.push({ title: 'Employee', items: employeeItems });
        }

        // Passport Data
        const passportItems: DetailItem[] = [];
        if (dataObj.passportData) {
          addItem(passportItems, 'Passport First Name', dataObj.passportData.passportFirstName);
          addItem(passportItems, 'Passport Middle Name', dataObj.passportData.passportMiddleName);
          addItem(passportItems, 'Passport Last Name', dataObj.passportData.passportLastName);
          addItem(
            passportItems,
            'Passport Issue Date',
            dataObj.passportData.passportIssueDate,
            true
          );
          addItem(
            passportItems,
            'Passport Expiry Date',
            dataObj.passportData.passportExpiryDate,
            true
          );
          addItem(passportItems, 'Date of Birth', dataObj.passportData.passportDateOfBirth, true);
          addItem(passportItems, 'Passport Gender', dataObj.passportData.passportGender);
          addItem(passportItems, 'Passport Nationality', dataObj.passportData.passportNationality);
          addItem(
            passportItems,
            'Issuing Authority',
            dataObj.passportData.passportIssuingAuthority
          );
          addItem(passportItems, 'Issuing Country', dataObj.passportData.passportIssuingCountry);
          addItem(passportItems, 'Place of Birth', dataObj.passportData.passportPlaceOfBirth);
        }
        if (passportItems.length > 0) {
          sections.push({ title: 'Passport', items: passportItems });
        }

        // EID Data
        const eidItems: DetailItem[] = [];
        if (dataObj.eidData) {
          addItem(eidItems, 'EID First Name', dataObj.eidData.eidFirstName);
          addItem(eidItems, 'EID Middle Name', dataObj.eidData.eidMiddleName);
          addItem(eidItems, 'EID Last Name', dataObj.eidData.eidLastName);
          addItem(eidItems, 'EID Number', dataObj.eidData.eidNumber);
          addItem(eidItems, 'EID Issue Date', dataObj.eidData.eidIssueDate, true);
          addItem(eidItems, 'EID Expiry Date', dataObj.eidData.eidExpiryDate, true);
          addItem(eidItems, 'Date of Birth', dataObj.eidData.eidDateOfBirth, true);
          addItem(eidItems, 'Gender', dataObj.eidData.eidGender);
          addItem(eidItems, 'Nationality', dataObj.eidData.eidNationality);
          addItem(eidItems, 'Place Issued', dataObj.eidData.eidPlaceIssued);
          addItem(eidItems, 'Occupation', dataObj.eidData.eidOccupation);
          addItem(eidItems, 'EID Card Number', dataObj.eidData.eidCardNumber);
        }
        if (eidItems.length > 0) {
          sections.push({ title: 'Emirates ID', items: eidItems });
        }

        // Visa Data
        const visaItems: DetailItem[] = [];
        if (dataObj.visaData) {
          addItem(visaItems, 'Visa Emirate', dataObj.visaData.visaEmirate);
          addItem(visaItems, 'Visa Designation', dataObj.visaData.visaDesignation);
        }
        if (visaItems.length > 0) {
          sections.push({ title: 'Visa', items: visaItems });
        }

        // Driver's License Data
        const driversLicenseItems: DetailItem[] = [];
        if (dataObj.driversLicense) {
          addItem(driversLicenseItems, 'Full Name', dataObj.driversLicense.dlFullName);
          addItem(driversLicenseItems, 'Category 1', dataObj.driversLicense.dlCategory1);
          addItem(driversLicenseItems, 'Category 2', dataObj.driversLicense.dlCategory2);
          addItem(driversLicenseItems, 'License Number', dataObj.driversLicense.dlNumber);
          addItem(driversLicenseItems, 'Issue Date', dataObj.driversLicense.dlIssueDate, true);
          addItem(driversLicenseItems, 'Expiry Date', dataObj.driversLicense.dlExpiryDate, true);
          addItem(driversLicenseItems, 'Date of Birth', dataObj.driversLicense.dlDateOfBirth, true);
          addItem(driversLicenseItems, 'Place Issued', dataObj.driversLicense.dlPlaceIssued);
        }
        if (driversLicenseItems.length > 0) {
          sections.push({ title: "Driver's License", items: driversLicenseItems });
        }

        // Contract Data
        const contractItems: DetailItem[] = [];
        if (dataObj.contract) {
          addItem(contractItems, 'Notice Period (days)', dataObj.contract.noticePeriod);
          addItem(contractItems, 'Probation Period (days)', dataObj.contract.probationPeriod);
          addItem(contractItems, 'Rest Days', dataObj.contract.restDays);
          addItem(contractItems, 'Basic Salary Amount', dataObj.contract.basicSalaryAmount);
          addItem(contractItems, 'Basic Salary Frequency', dataObj.contract.basicSalaryFrequency);
          addItem(contractItems, 'Housing Amount', dataObj.contract.housingAmount);
          addItem(contractItems, 'Housing Frequency', dataObj.contract.housingFrequency);
          addItem(
            contractItems,
            'Housing Provided by Company?',
            dataObj.contract.housingProvidedByCompany
          );
          addItem(
            contractItems,
            'Transport Allowance Amount',
            dataObj.contract.transportAllowanceAmount
          );
          addItem(
            contractItems,
            'Transport Allowance Frequency',
            dataObj.contract.transportAllowanceFrequency
          );
          addItem(contractItems, 'Other Allowance Amount', dataObj.contract.otherAllowanceAmount);
          addItem(
            contractItems,
            'Other Allowance Frequency',
            dataObj.contract.otherAllowanceFrequency
          );
          addItem(
            contractItems,
            'Children School Allowance Amount',
            dataObj.contract.childrenSchoolAllowanceAmount
          );
          addItem(
            contractItems,
            'Telephone Allowance Amount',
            dataObj.contract.telephoneAllowanceAmount
          );
          addItem(
            contractItems,
            'Telephone Allowance Frequency',
            dataObj.contract.telephoneAllowanceFrequency
          );
          addItem(contractItems, 'Ticket Allowance Amount', dataObj.contract.ticketAllowanceAmount);
          addItem(
            contractItems,
            'Ticket Allowance Frequency',
            dataObj.contract.ticketAllowanceFrequency
          );
          addItem(contractItems, 'Commission Amount', dataObj.contract.commissionAmount);
          addItem(contractItems, 'Commission Frequency', dataObj.contract.commissionFrequency);
        }
        if (contractItems.length > 0) {
          sections.push({ title: 'Contract', items: contractItems });
        }

        // Recruiter Info
        const recruiterItems: DetailItem[] = [];
        if (dataObj.recruiterInfo) {
          addItem(recruiterItems, 'Recruiter Name', dataObj.recruiterInfo.recruiterName);
          addItem(recruiterItems, 'Recruiter Email', dataObj.recruiterInfo.recruiterEmail);
          addItem(recruiterItems, 'Recruiter Telephone', dataObj.recruiterInfo.recruiterTelephone);
        }
        if (recruiterItems.length > 0) {
          sections.push({ title: 'Recruiter', items: recruiterItems });
        }

        // Religion Data
        const religionItems: DetailItem[] = [];
        if (dataObj.religion) {
          addItem(religionItems, 'Religion Code', dataObj.religion.religionCode);
          addItem(religionItems, 'Religion Name', dataObj.religion.religionName);
          addItem(religionItems, 'Sect Code', dataObj.religion.sectCode);
          addItem(religionItems, 'Sect Name', dataObj.religion.sectName);
        }
        if (religionItems.length > 0) {
          sections.push({ title: 'Religion', items: religionItems });
        }

        // Snapshot Table Data
        const snapshotItems: DetailItem[] = [];
        if (dataObj.snapshotTable) {
          addItem(snapshotItems, 'Application Status', dataObj.snapshotTable.applicationStatus);
          addItem(snapshotItems, 'Application Type', dataObj.snapshotTable.applicationType);
          addItem(snapshotItems, 'Application Cost', dataObj.snapshotTable.applicationCost);
          addItem(snapshotItems, 'Created Timestamp', dataObj.snapshotTable.createdTimestamp, true);
          addItem(snapshotItems, 'Status Timestamp', dataObj.snapshotTable.statusTimestamp, true);
        }
        if (snapshotItems.length > 0) {
          sections.push({ title: 'Snapshot', items: snapshotItems });
        }

        // Mapping Data
        const mappingItems: DetailItem[] = [];
        if (dataObj.mapping) {
          addItem(mappingItems, 'Prof Code', dataObj.mapping.profCode);
          addItem(mappingItems, 'Prof Contract', dataObj.mapping.profContract);
          addItem(mappingItems, 'Prof GDRFA', dataObj.mapping.profGdrfa);
          addItem(mappingItems, 'Prof GDRFA Code', dataObj.mapping.profGdrfaCode);
          addItem(mappingItems, 'Prof HRMS', dataObj.mapping.profHrms);
          addItem(mappingItems, 'Prof Preferred', dataObj.mapping.profPreferred);
          addItem(mappingItems, 'Prof BU', dataObj.mapping.profBu);
          addItem(mappingItems, 'Dept Code', dataObj.mapping.deptCode);
        }
        if (mappingItems.length > 0) {
          sections.push({ title: 'Mapping', items: mappingItems });
        }

        // Flight Info Data
        const flightItems: DetailItem[] = [];
        if (dataObj.flightInfo) {
          addItem(flightItems, 'Airline Name', dataObj.flightInfo.airlineName);
          addItem(flightItems, 'Flight Number', dataObj.flightInfo.flightNumber);
          addItem(flightItems, 'Departure Airport', dataObj.flightInfo.departureAirport);
          addItem(flightItems, 'Arrival Airport', dataObj.flightInfo.arrivalAirport);
          addItem(flightItems, 'Arrival Date', dataObj.flightInfo.arrivalDate, true);
          addItem(flightItems, 'Arrival Time', dataObj.flightInfo.arrivalTime);
          addItem(flightItems, 'Booking Ref', dataObj.flightInfo.bookingRef);
          addItem(flightItems, 'Last Updated', dataObj.flightInfo.lastUpdated, true);
          addItem(flightItems, 'Updated By', dataObj.flightInfo.updatedBy);
        }
        if (flightItems.length > 0) {
          sections.push({ title: 'Flight Info', items: flightItems });
        }

        // Ticket Management Data
        const ticketItems: DetailItem[] = [];
        if (dataObj.ticketManagement) {
          addItem(ticketItems, 'Incident Number', dataObj.ticketManagement.incidentNumber);
          addItem(ticketItems, 'Subject', dataObj.ticketManagement.subject);
          addItem(ticketItems, 'Description', dataObj.ticketManagement.description);
          addItem(
            ticketItems,
            'Requester Organisation',
            dataObj.ticketManagement.requesterOrganisation
          );
          addItem(ticketItems, 'Requester Name', dataObj.ticketManagement.requesterName);
          addItem(ticketItems, 'Requester Email', dataObj.ticketManagement.requesterEmail);
          addItem(ticketItems, 'Requester Phone', dataObj.ticketManagement.requesterPhone);
          addItem(ticketItems, 'Status', dataObj.ticketManagement.status);
          addItem(ticketItems, 'Probot ID', dataObj.ticketManagement.probotId);
          addItem(ticketItems, 'Client Priority', dataObj.ticketManagement.clientPriority);
          addItem(ticketItems, 'Probot Priority', dataObj.ticketManagement.probotPriority);
          addItem(ticketItems, 'Assigned To', dataObj.ticketManagement.assignedTo);
          addItem(ticketItems, 'Related Service Code', dataObj.ticketManagement.relatedServiceCode);
          addItem(ticketItems, 'Resolution Summary', dataObj.ticketManagement.resolutionSummary);
          addItem(ticketItems, 'Approval Status', dataObj.ticketManagement.approvalStatus);
          addItem(ticketItems, 'Escalation Level', dataObj.ticketManagement.escalationLevel);
          addItem(ticketItems, 'Attachments', dataObj.ticketManagement.attachments);
          addItem(ticketItems, 'Feedback Rating', dataObj.ticketManagement.feedbackRating);
          addItem(ticketItems, 'Root Cause Category', dataObj.ticketManagement.rootCauseCategory);
          addItem(ticketItems, 'Linked Tickets', dataObj.ticketManagement.linkedTickets);
          addItem(ticketItems, 'Automation?', dataObj.ticketManagement.automationFlag);
          addItem(ticketItems, 'Open Timestamp', dataObj.ticketManagement.openTimestamp, true);
          addItem(ticketItems, 'Last Update', dataObj.ticketManagement.lastUpdateTimestamp, true);
          addItem(ticketItems, 'Closed Timestamp', dataObj.ticketManagement.closedTimestamp, true);
          addItem(
            ticketItems,
            'Time to First Response (min)',
            dataObj.ticketManagement.timeToFirstResponse
          );
          addItem(
            ticketItems,
            'Time to Resolution (min)',
            dataObj.ticketManagement.timeToResolution
          );
        }
        if (ticketItems.length > 0) {
          sections.push({ title: 'Ticket Management', items: ticketItems });
        }

        return sections;
      },
    [t]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('common.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.details.title', { entity: t('visaApplications.title') })} />
        <HeaderRow
          title={t('crud.details.title', { entity: t('visaApplications.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('visaApplications.title') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('visaApplications.title') })}
        showBackButton={true}
        trailTarget={id}
      />
      <div className="row mt-3">
        <VisaApplicationDetailsComponent
          sections={prepareDetailsData(data?.data)}
          videoRecordings={data?.data?.videoRecordings}
          errorLogs={data?.data?.errorLogs}
          success={data?.success ?? false}
          isLoading={isLoading}
          error={error}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(memo(VisaApplicationDetailsContainer), 'visaApplication.view');
